import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

import SalariedAgentsList from "../../features/salaried-agents";

import SearchBar from "../../components/SearchBar";
import {
  useAgentListMutation,
  useChangeUserStatusMutation,
  useExportCsvMutation,
  useLazyDeleteUserQuery,
  useUserApprovalMutation,
} from "../../service/users";
import { AgentList, CommonBody, Permission } from "../../types/General";
import { errorToast, successToast } from "../../helpers";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { UPDATE_TYPES, USER_ROLES } from "../../utils/enums";
import { ConfirmationModal } from "../../Modals/confirmationModal";
import { isValidInput } from "../../utils/validations";
import useAuth from "../../hooks/useAuth";
import moment from "moment";

const Agents = () => {
  const navigate = useNavigate();
  const userData = useAuth();
  const [hidePermission, setHidePermission] = useState<Permission>();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const [modalType, setModalType] = useState("");

  const [agentList] = useAgentListMutation();
  const [deleteUser] = useLazyDeleteUserQuery();
  const [changeStatus] = useChangeUserStatusMutation();
  const [userApproval] = useUserApprovalMutation();
  const [exportCsv] = useExportCsvMutation();
  // const [agentCount] = useLazyAgentCountQuery();

  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<AgentList[]>([]);
  const [page, setPage] = useState<number>(1);

  // <--------EXPORT CSV DATE---------->
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // List of years (e.g., last 30 years)
  const years = Array.from({ length: 30 }, (_, i) => currentYear - i);

  // List of months, filtered if current year
  const allMonths = moment.months().map((label, index) => ({
    label,
    value: index + 1,
  }));

  const filteredMonths = allMonths.filter((m) => {
    if (parseInt(year) === currentYear) {
      return m.value <= currentMonth;
    }
    return true;
  });

  // Returns the 1st day of selected month
  const getMinDate = () => {
    return moment(`${year}-01-01`).format("YYYY-MM-DD"); // Start of selected year
  };

  const getMaxDate = () => {
    const today = moment();
    const selectedYear = parseInt(year);

    if (selectedYear === today.year()) {
      return today.format("YYYY-MM-DD");
    }
    return moment(`${year}-12-31`).format("YYYY-MM-DD"); // End of year
  };

  // Auto-set from/to date when year or month changes
  useEffect(() => {
    if (year && month) {
      const start = moment(`${year}-${String(month).padStart(2, "0")}-01`);
      const today = moment();
      const isCurrentMonth =
        start.isSame(today, "month") && start.isSame(today, "year");

      const end = isCurrentMonth ? today : moment(start).endOf("month");

      setFromDate(start.format("YYYY-MM-DD"));
      setToDate(end.format("YYYY-MM-DD"));
    }
  }, [year, month]);

  // <--------EXPORT CSV DATE---------->
  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const [mainTab] = useState(0);
  const [freelanceTab, setFreelanceTab] = useState(0);

  const handleDeleteUser = async () => {
    setLoading(true);
    try {
      const response = await deleteUser({
        id: selectedId,
      }).unwrap();
      setLoading(false);

      if (response?.status === 200) {
        successToast("Deleted successfully");
        getUsersList();
      } else {
        setRows([]);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const handleUserUpdate = async (id: number, status: any, type: string) => {
    const body = {
      ...(type === UPDATE_TYPES.BLOCK ? { is_active: status } : {}),
    };

    try {
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await changeStatus({
        body: encryptedBody,
        id: id,
      }).unwrap();
      if (response?.status === 200) {
        const updatedData = rows?.map((item) => {
          if (item?.id === id) {
            return {
              ...item,
              is_active: status,
            };
          }
          return item;
        });
        setRows(updatedData);
        if (status === true) {
          successToast("Status enabled successfully");
        } else {
          successToast("Status disabled successfully");
        }
      } else {
        errorToast("Please try again");
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
    }
  };

  const getUsersList = async () => {
    setLoading(true);
    try {
      const body = {
        start: page,
        length: 10,
        search: debouncedSearchTerm.trim(),
        agent_type: mainTab + 1,
        ...(fromDate && toDate ? { from_date: fromDate, to_date: toDate } : {}),
        ...(mainTab === 1 ? { freelancer_agent_type: freelanceTab + 1 } : {}),
      };

      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await agentList(encryptedBody).unwrap();
      setLoading(false);

      if (response?.status === 200) {
        setRows(response?.data?.response_object || []);
        setCount(response?.data?.total_records);
      } else {
        setRows([]);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const handleApproveReject = async (reason: string | undefined) => {
    const body = {
      is_approved: modalType === "accept" ? true : false,
      reject_reason: reason || "",
    };
    try {
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await userApproval({
        body: encryptedBody,
        id: selectedId,
      }).unwrap();
      if (response?.status === 200) {
        successToast(
          modalType === "accept"
            ? "Member approved successfully"
            : "Member rejected successfully"
        );
        if (modalType === "accept") {
          setFreelanceTab(1);
          setPage(1);
        } else {
          setFreelanceTab(2);
          setPage(1);
        }
      } else {
        errorToast("Please try again");
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
    }
  };

  const handleExportCSV = async () => {
    setLoading(true);
    try {
      const body = {
        role:
          mainTab === 0
            ? USER_ROLES.SALARIED_AGENT
            : USER_ROLES.FREELANCER_AGENT,
        ...(mainTab === 1 ? { status: freelanceTab + 1 } : {}),
      };

      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await exportCsv(encryptedBody).unwrap();
      setLoading(false);
      if (response?.status === 200) {
        window.open(response?.file_urls);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  useEffect(() => {
    getUsersList();
    // fetchCount();
  }, [debouncedSearchTerm, page, mainTab, freelanceTab, fromDate, toDate]);

  const checkPermission = () => {
    const permission = userData?.role_permission;
    if (permission?.length) {
      const foundPermission = permission?.find(
        (ele: Permission) => ele?.module === "Users"
      );

      if (foundPermission) {
        setHidePermission(foundPermission);
      } else {
        navigate(-1);
      }
    }
  };

  useEffect(() => {
    if (userData?.user_role_type === USER_ROLES.SUBADMIN) {
      checkPermission();
    }
  }, [userData]);

  useEffect(() => {
    if (typeof hidePermission !== "undefined") {
      if (!hidePermission?.can_view) {
        navigate("/permission-denied", { replace: true });
      }
    }
  }, [hidePermission, userData]);

  return (
    <MainContainer>
      <div className="main_title">
        <h1>Wizard Team</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> - Users
          - Wizard Team
        </p>
      </div>
      <div className="cards">
        <Box className="custom_tabs" sx={{ width: "100%" }}>
          <div className="table_header">
            <div className="left_s">
              <Box className="search_box">
                <SearchBar
                  searchTerm={searchTerm}
                  placeholder="Search"
                  setDebouncedSearchTerm={setDebouncedSearchTerm}
                  value={searchTerm}
                  onCross={() => setSearchTerm("")}
                  onChange={(val: any) => {
                    if (isValidInput(val.target.value)) {
                      setSearchTerm(val.target.value);
                    }
                  }}
                />
              </Box>
            </div>
            <div className="right_s fdx button_grp">
              <Box
                display="flex"
                gap={1.5}
                flexWrap="nowrap"
                alignItems="center"
              >
                <FormControl
                  size="small"
                  className="btn btn_highlight"
                  sx={{ minWidth: 100 }}
                >
                  <Select
                    // labelId="year-label"
                    value={year}
                    onChange={(e) => {
                      setYear(e.target.value);
                      setMonth("");
                      setFromDate("");
                      setToDate("");
                    }}
                    // label="Year"
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select Year
                    </MenuItem>
                    {years.map((y) => (
                      <MenuItem key={y} value={y.toString()}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {year && (
                  <FormControl
                    size="small"
                    className="btn btn_highlight"
                    sx={{ minWidth: 120 }}
                  >
                    {/* <InputLabel id="month-label">Month</InputLabel> */}
                    <Select
                      value={month}
                      onChange={(e) => setMonth(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select Month
                      </MenuItem>
                      {filteredMonths.map((m) => (
                        <MenuItem key={m.value} value={m.value}>
                          {m.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {year && month && (
                  <>
                    <TextField
                      type="date"
                      fullWidth
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      inputProps={{
                        min: getMinDate(),
                        max: getMaxDate(),
                      }}
                    />

                    <TextField
                      type="date"
                      fullWidth
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      inputProps={{
                        min: fromDate || getMinDate(),
                        max: getMaxDate(),
                      }}
                    />
                  </>
                )}
              </Box>
              {userData?.user_role_type === USER_ROLES.ADMIN ||
              hidePermission?.can_add_edit ? (
                <Button
                  className="btn btn_highlight"
                  onClick={() => navigate("/users/agents/add")}
                >
                  Add Member
                </Button>
              ) : null}
              <Button className="btn btn_highlight" onClick={handleExportCSV}>
                <img src="/static/images/upload_icon.svg" alt="Icon" /> Export
                CSV
              </Button>
            </div>
          </div>

          <SalariedAgentsList
            rows={rows}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            setModalType={setModalType}
            setOpen={setOpen}
            count={count}
            page={page}
            handlePageChange={handlePageChange}
            loading={loading}
            handleUserUpdate={handleUserUpdate}
            hidePermission={hidePermission}
          />
          {/* </CustomTabPanel> */}

          {/* Freelancing Agents with Nested Tabs */}
          {/* <CustomTabPanel value={mainTab} index={1}>
            <Tabs
              value={freelanceTab}
              onChange={(_, newValue) => setFreelanceTab(newValue)}
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label={`New Freelancers (${newFreelancerAgentCount})`} />
              <Tab
                label={`Approved Freelancers (${approvedFreelancerAgentCount})`}
              />
              <Tab
                label={`Rejected Freelancers (${rejectedFreelancerAgentCount})`}
              />
            </Tabs>

            <CustomTabPanel value={freelanceTab} index={0}>
              <NewFreelancersList
                rows={rows}
                selectedId={selectedId}
                setSelectedId={setSelectedId}
                setModalType={setModalType}
                setOpen={setOpen}
                count={count}
                page={page}
                handlePageChange={handlePageChange}
                loading={loading}
                handleUserUpdate={handleUserUpdate}
                hidePermission={hidePermission}
              />
            </CustomTabPanel>
            <CustomTabPanel value={freelanceTab} index={1}>
              <ApprovedFreelancersList
                rows={rows}
                selectedId={selectedId}
                setSelectedId={setSelectedId}
                setModalType={setModalType}
                setOpen={setOpen}
                count={count}
                page={page}
                handlePageChange={handlePageChange}
                loading={loading}
                handleUserUpdate={handleUserUpdate}
                hidePermission={hidePermission}
              />
            </CustomTabPanel>
            <CustomTabPanel value={freelanceTab} index={2}>
              <RejectedFreelancersList
                rows={rows}
                selectedId={selectedId}
                setSelectedId={setSelectedId}
                setModalType={setModalType}
                setOpen={setOpen}
                count={count}
                page={page}
                handlePageChange={handlePageChange}
                loading={loading}
                handleUserUpdate={handleUserUpdate}
                hidePermission={hidePermission}
              />
            </CustomTabPanel>
          </CustomTabPanel> */}
        </Box>

        <ConfirmationModal
          text={
            modalType === "delete"
              ? "Are you sure to delete this wizard team member?"
              : modalType === "accept"
                ? "Are you sure to approve this wizard team member?"
                : modalType === "reject"
                  ? "Are you sure to reject this wizard team member?"
                  : ""
          }
          reject={modalType === "reject" ? true : false}
          openModal={open}
          setOpenModal={setOpen}
          handleSubmit={
            modalType === "delete"
              ? handleDeleteUser
              : modalType === "accept"
                ? (reason) => handleApproveReject(reason)
                : modalType === "reject"
                  ? (reason) => handleApproveReject(reason)
                  : () => {}
          }
        />
      </div>
    </MainContainer>
  );
};

export default Agents;
